// New Product Approvals list - purchase manager and admin only. Coaches /
// medical incharges just submit (New Product Approval button on their
// Purchase page) and never see this list.
//   mode="pm"    purchase manager - all suggestions; accepts / rejects the pending ones
//   mode="admin" admin - every suggestion. Rows the purchase manager approved are
//                highlighted; the admin can also approve the rest directly (filling
//                max price + vendor), even ones the purchase manager rejected.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Eye, ChevronLeft, ChevronRight, CheckCircle2, BadgeCheck, XCircle, Clock } from 'lucide-react';
import { getProductApprovals } from '../../../api';
import NewProductApprovalModal from './NewProductApprovalModal';
import { pmDecisionOf, isRejectedByPm, isAdminActionable } from './productApprovalReview';
import './PurchasePanel.css';

const ITEMS_PER_PAGE = 10;

const STATUS_META = {
    pending_pm: { label: 'Pending (Purchase Manager)', tone: 'yellow' },
    pending_admin: { label: 'Pending (Admin)', tone: 'orange' },
    approved: { label: 'Approved', tone: 'green' },
    rejected: { label: 'Rejected', tone: 'red' },
};

// Admin's "Purchase Manager" column + filter, keyed by pmDecisionOf() ('none' = not reviewed)
const PM_REVIEW_META = {
    approved: { label: 'Approved', filterLabel: 'Approved by Purchase Manager', tone: 'green', Icon: BadgeCheck },
    rejected: { label: 'Rejected', filterLabel: 'Rejected by Purchase Manager', tone: 'red', Icon: XCircle },
    none: { label: 'Not reviewed', filterLabel: 'Not reviewed by Purchase Manager', tone: 'grey', Icon: Clock },
};

const CATEGORY_TONE = {
    'ISF Shop': 'pink',
    Consumables: 'blue',
    Infra: 'teal',
    Medicines: 'purple',
    Repairs: 'orange',
    Others: 'grey',
};

const PRIORITY_COLOR = { high: '#dc2626', medium: '#b45309', low: '#2563eb' };

const formatDate = (date) => {
    if (!date) return 'N/A';
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? 'N/A' : parsed.toLocaleDateString('en-IN');
};

const statusLabel = (item) => {
    if (item.status === 'rejected') {
        return item.rejectedStage === 'admin' ? 'Rejected by Admin' : 'Rejected by Purchase Manager';
    }
    if (item.status === 'approved' && pmDecisionOf(item) !== 'approved') return 'Approved by Admin (direct)';
    return STATUS_META[item.status]?.label || item.status;
};

export default function ProductApprovalsView({ mode = 'pm', balagruhas = [], refreshKey = 0 }) {
    const isAdmin = mode === 'admin';
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    // The purchase manager lands on what is waiting for them; the admin sees everything.
    const [statusFilter, setStatusFilter] = useState(isAdmin ? 'all' : 'pending_pm');
    const [pmFilter, setPmFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getProductApprovals();
            setItems(response?.success ? response.data || [] : []);
        } catch (error) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load, refreshKey]);

    const filtered = useMemo(() => items.filter((item) => {
        if (statusFilter !== 'all' && item.status !== statusFilter) return false;
        if (pmFilter !== 'all' && (pmDecisionOf(item) || 'none') !== pmFilter) return false;
        if (search.trim()) {
            const haystack = `${item.approvalId || ''} ${item.productName || ''} ${item.requestedBy?.name || ''} ${item.balagruhaId?.name || ''} ${item.vendor?.name || ''}`.toLowerCase();
            if (!haystack.includes(search.trim().toLowerCase())) return false;
        }
        return true;
    }), [items, statusFilter, pmFilter, search]);

    const pendingCount = items.filter((item) =>
        isAdmin ? item.status === 'pending_pm' || item.status === 'pending_admin' : item.status === 'pending_pm'
    ).length;
    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const pageItems = filtered.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);
    const columnCount = isAdmin ? 12 : 11;

    const modalModeFor = (item) => {
        if (mode === 'pm' && item.status === 'pending_pm') return 'pm-review';
        if (isAdmin && isAdminActionable(item)) return 'admin-review';
        return 'view';
    };

    // Admin: green for rows the purchase manager approved, red while a
    // purchase manager rejection still stands.
    const rowClassFor = (item) => {
        if (!isAdmin) return undefined;
        if (pmDecisionOf(item) === 'approved') return 'pp-row-pm-approved';
        if (isRejectedByPm(item)) return 'pp-row-pm-rejected';
        return undefined;
    };

    return (
        <>
            <div className="pp-subheader">
                <h3>
                    {isAdmin ? 'New Product Approvals' : 'Pending Approvals'}
                    {pendingCount > 0 && (
                        <span className="pp-badge pp-badge-orange" style={{ marginLeft: 10 }}>{pendingCount} to review</span>
                    )}
                </h3>
            </div>

            <div className="pp-filters">
                <div className="pp-filter-field">
                    <label>Status</label>
                    <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                        <option value="all">All Status</option>
                        {Object.keys(STATUS_META).map((value) => (
                            <option key={value} value={value}>{STATUS_META[value].label}</option>
                        ))}
                    </select>
                </div>
                {isAdmin && (
                    <div className="pp-filter-field">
                        <label>Purchase Manager Review</label>
                        <select value={pmFilter} onChange={(e) => { setPmFilter(e.target.value); setPage(1); }}>
                            <option value="all">All</option>
                            {Object.entries(PM_REVIEW_META).map(([value, meta]) => (
                                <option key={value} value={value}>{meta.filterLabel}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <div className="pp-search-row">
                <div className="pp-search-box">
                    <Search size={16} className="pp-search-icon" />
                    <input
                        type="text"
                        placeholder="Search by product, requester, balagruha..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                </div>
            </div>

            {isAdmin && (
                <div className="pp-pm-legend">
                    <span><i className="pp-pm-swatch pp-pm-swatch-approved" /> Approved by Purchase Manager - max price &amp; vendor already filled</span>
                    <span><i className="pp-pm-swatch pp-pm-swatch-rejected" /> Rejected by Purchase Manager - you can still approve it</span>
                    <span>To approve any other product directly, you fill in the max price &amp; vendor</span>
                </div>
            )}

            <div className="pp-table-wrapper">
                <table className="pp-table">
                    <thead>
                        <tr>
                            <th>Sr. No.</th>
                            <th>Requested By</th>
                            <th>Balagruha</th>
                            <th>Date</th>
                            <th>Due Date</th>
                            <th>Product Name</th>
                            <th>Category</th>
                            <th>Quantity</th>
                            <th>Max Price</th>
                            {isAdmin && <th>Purchase Manager</th>}
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={columnCount} className="pp-empty-cell">Loading...</td></tr>
                        ) : pageItems.length === 0 ? (
                            <tr><td colSpan={columnCount} className="pp-empty-cell">No product approvals found</td></tr>
                        ) : (
                            pageItems.map((item, index) => {
                                const canReview = modalModeFor(item) !== 'view';
                                const pmReview = PM_REVIEW_META[pmDecisionOf(item) || 'none'];
                                return (
                                    <tr key={item._id} className={rowClassFor(item)}>
                                        <td>{(safePage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                                        <td>{item.requestedBy?.name || 'N/A'}</td>
                                        <td>{item.balagruhaId?.name || 'N/A'}</td>
                                        <td>{formatDate(item.createdAt)}</td>
                                        <td>{formatDate(item.deadline)}</td>
                                        <td style={{ color: PRIORITY_COLOR[item.priority] || '#334155', fontWeight: 700 }}>
                                            {item.productName}
                                        </td>
                                        <td>
                                            <span className={`pp-badge pp-badge-${CATEGORY_TONE[item.category] || 'grey'}`}>
                                                {item.category}
                                            </span>
                                        </td>
                                        <td>{item.quantity}</td>
                                        <td>{item.maxPrice ? `₹${Number(item.maxPrice).toLocaleString('en-IN')}` : '-'}</td>
                                        {isAdmin && (
                                            <td>
                                                <span className={`pp-badge pp-badge-${pmReview.tone} pp-pm-chip`} title={pmReview.filterLabel}>
                                                    <pmReview.Icon size={13} /> {pmReview.label}
                                                </span>
                                            </td>
                                        )}
                                        <td>
                                            <span className={`pp-badge pp-badge-${STATUS_META[item.status]?.tone || 'grey'}`}>
                                                {statusLabel(item)}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="pp-icon-btn"
                                                title={canReview ? 'Review' : 'View details'}
                                                onClick={() => setSelected(item)}
                                            >
                                                {canReview ? <CheckCircle2 size={15} /> : <Eye size={15} />}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {filtered.length > 0 && (
                <div className="pp-pagination">
                    <span className="pp-pagination-info">
                        Showing {(safePage - 1) * ITEMS_PER_PAGE + 1} to{' '}
                        {Math.min(safePage * ITEMS_PER_PAGE, filtered.length)} of {filtered.length} product approvals
                    </span>
                    <div className="pp-pagination-controls">
                        <button type="button" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>
                            <ChevronLeft size={16} />
                        </button>
                        <button type="button" className="active">{safePage}</button>
                        <button type="button" disabled={safePage === totalPages} onClick={() => setPage(safePage + 1)}>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}

            {selected && (
                <NewProductApprovalModal
                    mode={modalModeFor(selected)}
                    approval={selected}
                    balagruhas={balagruhas}
                    onClose={() => setSelected(null)}
                    onDone={() => { setSelected(null); load(); }}
                />
            )}
        </>
    );
}
